import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, Wallet, Plus, Trash2, Loader2, Landmark, CreditCard } from "lucide-react";

const rupiah = (n) => "Rp " + Math.round(Number(n || 0)).toLocaleString("id-ID");
const monthLabel = (ym) => { if (!ym || ym === "all") return "Semua Waktu"; try { const [y, m] = ym.split("-"); return new Date(y, m - 1, 1).toLocaleDateString("id-ID", { month: "long", year: "numeric" }); } catch { return ym; } };
const thisMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };

const QUICK = [
  { label: "Langganan Emergent" },
  { label: "Top-up Credits Emergent" },
  { label: "Biaya Deployment (50 credits)" },
  { label: "Domain fastrading.ai" },
  { label: "Iklan / Marketing" },
];

const Card = ({ icon: Icon, label, value, tone, testid }) => (
  <div className="card p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-xs text-[var(--text-muted)] mb-1"><Icon size={14} className={tone} /> {label}</div>
    <p className={`font-mono-data font-extrabold text-xl ${tone}`}>{value}</p>
  </div>
);

export default function AdminFinance() {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState("all");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ label: "", amount: "", month: thisMonth(), note: "" });
  const [adding, setAdding] = useState(false);

  const load = useCallback((m) => {
    setLoading(true);
    api.get(`/admin/finance?month=${m}`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(month); }, [month, load]);

  const addExpense = async (e) => {
    e.preventDefault();
    if (!form.label.trim() || !form.amount) { toast.error("Isi label & jumlah pengeluaran."); return; }
    setAdding(true);
    try {
      await api.post("/admin/expenses", { label: form.label.trim(), amount: Number(form.amount), month: form.month, note: form.note });
      toast.success("Pengeluaran ditambahkan.");
      setForm({ label: "", amount: "", month: form.month, note: "" });
      load(month);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menambah pengeluaran.");
    } finally { setAdding(false); }
  };

  const delExpense = async (id) => {
    try { await api.delete(`/admin/expenses/${id}`); toast.success("Pengeluaran dihapus."); load(month); }
    catch { toast.error("Gagal menghapus."); }
  };

  if (loading && !data) return <div className="card p-12 flex items-center justify-center"><Loader2 className="animate-spin text-accent" /></div>;
  if (!data) return null;

  const { revenue, expense_total, net, series, expenses, available_months } = data;
  const inp = "w-full h-10 rounded-lg bg-[var(--surface-base)] border border-[var(--border)] px-3 text-sm outline-none focus:border-[var(--accent)]/50";

  return (
    <div data-testid="admin-finance">
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <span className="text-sm text-[var(--text-secondary)]">Periode:</span>
        <select value={month} onChange={(e) => setMonth(e.target.value)} data-testid="finance-month-select" className="h-9 rounded-lg bg-[var(--surface-card)] border border-[var(--border)] px-3 text-sm outline-none">
          <option value="all">Semua Waktu</option>
          {available_months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
        </select>
        <span className="text-xs text-[var(--text-muted)]">· {monthLabel(month)}</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <Card icon={TrendingUp} label={`Pendapatan (${revenue.count} transaksi)`} value={rupiah(revenue.gross)} tone="text-bull" testid="finance-revenue" />
        <Card icon={TrendingDown} label="Total Pengeluaran" value={rupiah(expense_total)} tone="text-bear" testid="finance-expense" />
        <Card icon={Wallet} label="Laba Bersih" value={rupiah(net)} tone={net >= 0 ? "text-accent" : "text-bear"} testid="finance-net" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="card p-4">
          <p className="font-heading font-bold text-sm mb-3">Sumber Pendapatan</p>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between"><span className="flex items-center gap-2"><Landmark size={14} className="text-accent" /> Transfer BRI</span><span className="font-mono-data font-semibold">{rupiah(revenue.by_source.bri)}</span></div>
            <div className="flex items-center justify-between"><span className="flex items-center gap-2"><CreditCard size={14} className="text-accent" /> Stripe</span><span className="font-mono-data font-semibold">{rupiah(revenue.by_source.stripe)}</span></div>
          </div>
        </div>
        <div className="card p-4">
          <p className="font-heading font-bold text-sm mb-3">Pendapatan per Paket</p>
          {Object.keys(revenue.by_package).length === 0 ? (
            <p className="text-sm text-[var(--text-muted)]">Belum ada pendapatan pada periode ini.</p>
          ) : (
            <div className="space-y-2 text-sm">
              {Object.entries(revenue.by_package).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between"><span>{k} <span className="text-[var(--text-muted)]">×{v.count}</span></span><span className="font-mono-data font-semibold">{rupiah(v.amount)}</span></div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Pengeluaran */}
      <div className="card p-4 mb-6">
        <p className="font-heading font-bold text-sm mb-3">Tambah Pengeluaran</p>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {QUICK.map((q) => (
            <button key={q.label} onClick={() => setForm((f) => ({ ...f, label: q.label }))} data-testid={`quick-exp-${q.label}`} className="text-[11px] px-2.5 py-1 rounded-full bg-[var(--surface-hover)] border border-[var(--border)] hover:border-[var(--accent)]/40 hover:text-accent transition-colors">
              + {q.label}
            </button>
          ))}
        </div>
        <form onSubmit={addExpense} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end">
          <div className="sm:col-span-5"><span className="text-xs text-[var(--text-secondary)] mb-1 block">Label</span><input data-testid="exp-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="mis. Langganan Emergent" className={inp} /></div>
          <div className="sm:col-span-3"><span className="text-xs text-[var(--text-secondary)] mb-1 block">Jumlah (Rp)</span><input data-testid="exp-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0" className={inp} /></div>
          <div className="sm:col-span-2"><span className="text-xs text-[var(--text-secondary)] mb-1 block">Bulan</span><input data-testid="exp-month" type="month" value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} className={inp} /></div>
          <div className="sm:col-span-2"><button type="submit" disabled={adding} data-testid="exp-add-btn" className="w-full h-10 rounded-lg bg-[var(--accent)] text-[#07090E] font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-60">{adding ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Tambah</button></div>
        </form>

        {expenses.length > 0 && (
          <div className="mt-4 space-y-1.5">
            {expenses.map((e) => (
              <div key={e.id} className="flex items-center gap-3 bg-[var(--surface-base)] rounded-lg px-3 py-2 border border-[var(--border-subtle)]" data-testid={`exp-row-${e.id}`}>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium">{e.label}</span>
                  <span className="text-xs text-[var(--text-muted)] ml-2">{monthLabel(e.month)}{e.note ? ` · ${e.note}` : ""}</span>
                </div>
                <span className="font-mono-data text-sm font-semibold text-bear">{rupiah(e.amount)}</span>
                <button onClick={() => delExpense(e.id)} data-testid={`exp-del-${e.id}`} className="p-1.5 rounded-md hover:bg-[var(--bear)]/15 text-[var(--text-muted)] hover:text-bear transition-colors"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Rekap bulanan */}
      <div className="card p-4">
        <p className="font-heading font-bold text-sm mb-3">Rekap per Bulan</p>
        {series.length === 0 ? (
          <p className="text-sm text-[var(--text-muted)]">Belum ada data.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="finance-series">
              <thead>
                <tr className="text-left text-xs text-[var(--text-muted)] border-b border-[var(--border)]">
                  <th className="py-2 pr-4">Bulan</th>
                  <th className="py-2 pr-4 text-right">Pendapatan</th>
                  <th className="py-2 pr-4 text-right">Pengeluaran</th>
                  <th className="py-2 text-right">Laba Bersih</th>
                </tr>
              </thead>
              <tbody>
                {series.map((s) => (
                  <tr key={s.month} className="border-b border-[var(--border-subtle)]">
                    <td className="py-2 pr-4">{monthLabel(s.month)}</td>
                    <td className="py-2 pr-4 text-right font-mono-data text-bull">{rupiah(s.revenue)}</td>
                    <td className="py-2 pr-4 text-right font-mono-data text-bear">{rupiah(s.expense)}</td>
                    <td className={`py-2 text-right font-mono-data font-semibold ${s.net >= 0 ? "text-accent" : "text-bear"}`}>{rupiah(s.net)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
